import type { MediaRow } from '@/routes/media/types';
import type { PublishStatus } from '@/components/editor/PublishingSidebar';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { TaxonomyRef } from '@/routes/news/types';

/**
 * docs/09-BUILD-PLAN.md Phase 6 steps 6 and 7 — the academic structure.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}`.
 */

/** docs/02-DESIGN-SYSTEM.md §1.4's accent keys, fixed per faculty. */
export const FACULTY_ACCENTS = [
  'teal',
  'violet',
  'amber',
  'navy',
  'rose',
  'sky',
  'lime',
  'slate',
] as const;

/** docs/03-DATA-MODEL.md §4's `level` enum. */
export const PROGRAM_LEVELS = [
  'bachelor',
  'master',
  'phd',
  'integrated',
  'minor',
  'teacher_preparation',
  // Central to the university's mission in a multi-ethnic region, not an
  // afterthought (CLAUDE.md).
  'georgian_language_preparation',
  'lifelong',
] as const;

/** Akhaltsikhe and Akhalkalaki (CLAUDE.md). */
export const PROGRAM_CAMPUSES = ['akhaltsikhe', 'akhalkalaki'] as const;

/** §4's `kind` enum — the two blocks a faculty page draws. */
export const STATISTIC_KINDS = ['students', 'staff'] as const;

export type FacultyAccent = (typeof FACULTY_ACCENTS)[number];
export type ProgramLevel = (typeof PROGRAM_LEVELS)[number];
export type ProgramCampus = (typeof PROGRAM_CAMPUSES)[number];
export type StatisticKind = (typeof STATISTIC_KINDS)[number];

/** A person, as the API summarises them for a selector. */
export interface StaffRef {
  id: number;
  name: string;
}

export interface FacultyRow {
  id: number;
  name: TranslatedValue;
  short_name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  accent: FacultyAccent;
  icon: string | null;
  cover_path: string | null;
  /*
   * `cover_url`, `photo_url` and `logo_url` were removed here.
   *
   * Task #71 moved images onto the media library and the API resources
   * went from a resolved URL string to the media object above. These
   * types kept declaring the old fields, so three list cells went on
   * reading `undefined` and rendering their empty state — the news list
   * showed a placeholder against 676 articles that had a cover, and
   * nothing failed. Use `thumbUrl(row.cover)`.
   */
  /** The library reference that replaces `cover_path`. */
  cover_media_id: number | null;
  cover: MediaRow | null;
  dean_staff_id: number | null;
  dean: StaffRef | null;
  email: string | null;
  phone: string | null;
  room: string | null;
  established_year: number | null;
  sort_order: number;
  /** Null when the endpoint did not count — never read as zero. */
  programs_count: number | null;
  departments_count: number | null;
  staff_count: number | null;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: { id: number; name: string } | null;
}

export interface FacultyInput {
  name: TranslatedValue;
  short_name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  accent: FacultyAccent;
  icon: string | null;
  cover_media_id: number | null;
  dean_staff_id: number | null;
  email: string | null;
  phone: string | null;
  room: string | null;
  established_year: number | null;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

export interface DepartmentRow {
  id: number;
  faculty_id: number;
  faculty: TaxonomyRef | null;
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  head_staff_id: number | null;
  head: StaffRef | null;
  sort_order: number;
  programs_count: number | null;
  staff_count: number | null;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  /** No `deleted_at` — a department cascades from its faculty (§4). */
  deleted_at: null;
}

export interface DepartmentInput {
  faculty_id: number;
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  head_staff_id: number | null;
  sort_order: number;
}

export interface FacultyStatisticRow {
  id: number;
  faculty_id: number;
  faculty: TaxonomyRef | null;
  kind: StatisticKind;
  label: TranslatedValue;
  value: number;
  /** "2025-2026". §4 keeps a row per year rather than overwriting. */
  academic_year: string | null;
  sort_order: number;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: null;
}

export interface FacultyStatisticInput {
  faculty_id: number;
  kind: StatisticKind;
  label: TranslatedValue;
  value: number;
  academic_year: string | null;
  sort_order: number;
}

export interface ProgramRow {
  id: number;
  faculty_id: number;
  faculty: TaxonomyRef | null;
  department_id: number | null;
  department: TaxonomyRef | null;
  code: string | null;
  name: TranslatedValue;
  slug: TranslatedValue;
  level: ProgramLevel;
  campus: ProgramCampus;
  qualification: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  /** Rich text — docs/03-DATA-MODEL.md §4, unlike `required_exams`. */
  learning_outcomes: TranslatedValue;
  admission_requirements: TranslatedValue;
  required_exams: Record<string, string[] | null>;
  ects_total: number | null;
  ects_breakdown: unknown;
  duration_years: string | number | null;
  language: TranslatedValue;
  tuition_fee: number | null;
  head_staff_id: number | null;
  head: StaffRef | null;
  is_accredited: boolean;
  accreditation_until: string | null;
  /** Computed by the server, in the university's timezone rather than yours. */
  accreditation_expired: boolean;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: { id: number; name: string } | null;
}

export interface ProgramInput {
  faculty_id: number;
  department_id: number | null;
  code: string | null;
  name: TranslatedValue;
  slug: TranslatedValue;
  level: ProgramLevel;
  campus: ProgramCampus;
  qualification: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  /** Rich text — docs/03-DATA-MODEL.md §4, unlike `required_exams`. */
  learning_outcomes: TranslatedValue;
  admission_requirements: TranslatedValue;
  required_exams: Record<string, string[] | null>;
  ects_total: number | null;
  duration_years: number | null;
  language: TranslatedValue;
  tuition_fee: number | null;
  head_staff_id: number | null;
  is_accredited: boolean;
  accreditation_until: string | null;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}
